// Enhanced Reminders Service for Supabase + Render Backend
import { showToast } from "../utils/show-toast";
import { transformErrorResponse } from "../utils/transform-error-response";

import api from "./api";
import { getApiUrl } from "./config";
import { endpoint } from "./endpoints";
import { handleApiCall, normalizeApiError } from "../utils/apiResponseHandler";

// Enhanced Reminder Types
export interface ReminderAssociation {
  id?: string;
  reminderId?: string;
  entityType: string;
  entityId: string;
  notes?: string;
  metadata?: Record<string, unknown>;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string | null;
}

export interface Reminder {
  id: string;
  userId: string;
  title: string;
  date: string;
  text?: string;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
  associations?: ReminderAssociation[];
  description?: string;
  type?: 'appointment' | 'follow-up' | 'task' | 'deadline' | 'birthday' | 'custom';
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  dueDate?: Date;
  isCompleted?: boolean;
  isRecurring?: boolean;
  reminderTime?: Date;
  recurringPattern?: 'daily' | 'weekly' | 'monthly' | 'yearly';
  associatedClientId?: string;
  associatedClientName?: string;
  notes?: string;
  completedAt?: Date;
  status?: 'pending' | 'notified' | 'completed' | 'overdue' | 'cancelled';
}

export interface CreateReminderRequest {
  title: string;
  date: string;
  text?: string;
  remindAt?: string; // accepted by backend (ISO or YYYY-MM-DD)
  associations?: Omit<ReminderAssociation, 'id' | 'reminderId' | 'createdAt' | 'updatedAt' | 'deletedAt'>[];
  type?: Reminder['type'];
  priority?: Reminder['priority'];
  isRecurring?: boolean;
  recurringPattern?: Reminder['recurringPattern'];
  reminderTime?: string;
  notes?: string;
}

export interface UpdateReminderRequest {
  title?: string;
  date?: string;
  text?: string;
  notes?: string;
  remindAt?: string;
  metadata?: Record<string, unknown>;
  associations?: Omit<ReminderAssociation, 'id' | 'reminderId' | 'createdAt' | 'updatedAt' | 'deletedAt'>[];
  type?: Reminder['type'];
  priority?: Reminder['priority'];
  isCompleted?: boolean;
  isRecurring?: boolean;
  recurringPattern?: Reminder['recurringPattern'];
  reminderTime?: string;
  status?: Reminder['status'];
}

export interface RemindersResponse {
  statusCode: number;
  message: string;
  data: {
    meta: {
      page: number;
      nextPage: number | null;
      prevPage: number | null;
      offset: number;
      totalItems: number;
      totalPages: number;
      itemCount: number;
    };
    items: Reminder[];
  };
  timestamp: string;
  timeTakenMs: number;
  path: string;
}

export interface ReminderFilters {
  entityType?: string;
  status?: 'all' | 'pending' | 'notified' | 'completed' | 'overdue' | 'cancelled';
  dateRange?: 'today' | 'week' | 'month' | 'all';
  type?: string;
  priority?: string;
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// Enhanced Reminders Service Class
class EnhancedRemindersService {
  // Handle reminders errors
  private handleError(error: unknown, operation: string, opts: { toast?: boolean } = {}): never {

    const normalized = normalizeApiError(error);
    const message = normalized.message || `Failed to ${operation}`;
    const retrySuffix = typeof normalized.retryAfterSeconds === 'number'
      ? ` (retry after ${normalized.retryAfterSeconds}s)`
      : '';
    // silent = MFA-window 403 the interceptor recovers on its own — the request
    // is refetched once the gate reopens, so surfacing "Failed to fetch
    // reminders" at sign-in would be noise about a non-event.
    // A 401 belongs to the interceptor too (refresh or sign-out). And a failed
    // READ never toasts — the screen renders its own error state and retries.
    const is401 = normalized.code === 401 || String(normalized.code) === '401';
    if (opts.toast !== false && !normalized.silent && !is401) {
      showToast(`${message}${retrySuffix}`, "error");
    }

    const e = new Error(message) as Error & { code?: string | number; retryAfterSeconds?: number };
    e.code = normalized.code;
    e.retryAfterSeconds = normalized.retryAfterSeconds;
    throw e;
  }

  // Validate reminder data
  private validateReminderData(data: Partial<CreateReminderRequest | UpdateReminderRequest>, operation: string) {
    if (operation === 'create') {
      const createData = data as CreateReminderRequest;
      if (!createData.title?.trim()) {
        throw new Error('Reminder title is required');
      }
      if (!createData.date?.trim()) {
        throw new Error('Reminder date is required');
      }
      
      // Validate date format
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(createData.date)) {
        throw new Error('Invalid date format. Use YYYY-MM-DD format');
      }
      
      // Validate date is not in the past (optional, can be removed if past dates are allowed)
      // Parse YYYY-MM-DD as local date to avoid timezone issues
      const [year, month, day] = createData.date.split('-').map(Number);
      const reminderDate = new Date(year, month - 1, day);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (reminderDate < today) {

        // Don't throw an error, just warn - past dates might be valid for historical reminders
      }
    }
    
    if (operation === 'update') {
      const updateData = data as UpdateReminderRequest;
      if (updateData.title !== undefined && !updateData.title?.trim()) {
        throw new Error('Reminder title cannot be empty');
      }
      if (updateData.date !== undefined && !updateData.date?.trim()) {
        throw new Error('Reminder date cannot be empty');
      }
      
      // Validate date format if provided
      if (updateData.date) {
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
        if (!dateRegex.test(updateData.date)) {
          throw new Error('Invalid date format. Use YYYY-MM-DD format');
        }
      }
    }
  }

  // Get reminders with filtering and pagination
  async getReminders(filters?: ReminderFilters): Promise<RemindersResponse> {
    try {
      // Validate parameters
      if (filters?.page && filters.page < 1) {
        throw new Error('Page number must be greater than 0');
      }
      if (filters?.limit && (filters.limit < 1 || filters.limit > 1000)) {
        throw new Error('Limit must be between 1 and 1000');
      }

      const queryParams = new URLSearchParams();
      
      if (filters?.entityType) {
        queryParams.append("entityType", filters.entityType);
      }
      if (filters?.page) {
        queryParams.append("page", filters.page.toString());
      }
      if (filters?.limit) {
        queryParams.append("limit", filters.limit.toString());
      }
      if (filters?.search) {
        queryParams.append("search", filters.search);
        queryParams.append("q", filters.search); // Alternative parameter
      }
      if (filters?.status && filters.status !== 'all') {
        queryParams.append("status", filters.status);
      }
      if (filters?.type) {
        queryParams.append("type", filters.type);
      }
      if (filters?.priority) {
        queryParams.append("priority", filters.priority);
      }
      if (filters?.sortBy) {
        queryParams.append("sortBy", filters.sortBy);
        queryParams.append("sort", filters.sortBy); // Alternative parameter
      }
      if (filters?.sortOrder) {
        queryParams.append("sortOrder", filters.sortOrder);
        queryParams.append("order", filters.sortOrder); // Alternative parameter
      }
      if (filters?.dateRange && filters.dateRange !== 'all') {
        queryParams.append("dateRange", filters.dateRange);
      }

      const url = queryParams.toString() 
        ? `${getApiUrl(endpoint.reminders.getReminders)}?${queryParams.toString()}`
        : getApiUrl(endpoint.reminders.getReminders);

      const remindersData = await handleApiCall<RemindersResponse>(async () => {
        const response = await api.get<unknown>({
          url,
          auth: true,
        });
        return response;
      });

      // Handle different response structures after handleApiCall extraction
      let items: Reminder[] = [];
      let meta: any = {};
      
      if ((remindersData as any)?.data?.items && Array.isArray((remindersData as any).data.items)) {
        items = (remindersData as any).data.items;
        meta = (remindersData as any).data.meta || {};
      } else if ((remindersData as any)?.items && Array.isArray((remindersData as any).items)) {
        items = (remindersData as any).items;
        meta = (remindersData as any).meta || {};
      } else if (Array.isArray(remindersData)) {
        items = remindersData;
        meta = { page: 1, totalItems: remindersData.length, totalPages: 1, itemCount: remindersData.length };
      } else {

        items = [];
        meta = { page: 1, totalItems: 0, totalPages: 0, itemCount: 0 };
      }

      // Trust backend status - no client-side status computation
      // The backend handles: pending -> notified -> overdue transitions
      const enhancedItems = items;

      return {
        statusCode: 200,
        message: 'Reminders fetched successfully',
        data: {
          items: enhancedItems,
          meta: {
            page: meta.page || 1,
            totalItems: meta.totalItems || enhancedItems.length,
            totalPages: meta.totalPages || 1,
            itemCount: meta.itemCount || enhancedItems.length,
            nextPage: meta.nextPage || null,
            prevPage: meta.prevPage || null,
            offset: meta.offset || 0
          }
        },
        timestamp: new Date().toISOString(),
        timeTakenMs: 0,
        path: '/reminders'
      };
    } catch (error) {
      this.handleError(error, 'fetch reminders', { toast: false });
    }
  }

  // Get reminder by ID
  async getReminderById(reminderId: string): Promise<Reminder> {
    try {
      // Validate reminder ID
      if (!reminderId?.trim()) {
        throw new Error('Reminder ID is required');
      }

      const reminderData = await handleApiCall<Reminder>(async () => {
        const response = await api.get<{
          statusCode: number;
          message: string;
          data: Reminder;
          timestamp: string;
          timeTakenMs: number;
        }>({
          url: getApiUrl(endpoint.reminders.getReminderById(reminderId)),
          auth: true,
        });
        return response;
      });

      // Trust backend status - no client-side status computation
      return reminderData;
    } catch (error) {
      this.handleError(error, `fetch reminder ${reminderId}`);
    }
  }

  // Create reminder
  async createReminder(reminderData: CreateReminderRequest): Promise<Reminder> {
    try {
      // Validate reminder data
      const dataToValidate = reminderData as Partial<CreateReminderRequest | UpdateReminderRequest>;
      this.validateReminderData(dataToValidate, 'create');

      // Sanitize data - include all fields the backend DTO accepts
      const sanitizedData: Record<string, unknown> = {
        title: reminderData.title.trim(),
        date: reminderData.date,
        text: reminderData.text?.trim() || '',
      };

      // Optional fields - only include if they exist
      const maybe = reminderData as unknown as Record<string, unknown>;

      if (maybe.remindAt) {
        sanitizedData.remindAt = maybe.remindAt;
      }

      if (maybe.priority) {
        sanitizedData.priority = maybe.priority;
      }

      if (maybe.notes) {
        sanitizedData.notes = typeof maybe.notes === 'string' ? maybe.notes.trim() : maybe.notes;
      }

      // CRITICAL: Include reminderTime for proper notification scheduling
      if (maybe.reminderTime) {
        sanitizedData.reminderTime = maybe.reminderTime;
      }

      if (Array.isArray(maybe.associations) && maybe.associations.length > 0) {
        sanitizedData.associations = maybe.associations;
      }

      const createdReminder = await handleApiCall<Reminder>(async () => {
        const response = await api.post<{
          statusCode: number;
          message: string;
          data: Reminder;
          timestamp: string;
          timeTakenMs: number;
        }>({
          url: getApiUrl(endpoint.reminders.createReminder),
          body: sanitizedData,
          auth: true,
        });
        return response;
      });

      // Don't show toast here - let the component handle success notifications
      // showToast("Reminder created successfully", "success");
      return createdReminder;
    } catch (error) {
      this.handleError(error, 'create reminder');
    }
  }

  // Update reminder
  async updateReminder(reminderId: string, reminderData: UpdateReminderRequest): Promise<Reminder> {
    try {
      // Validate reminder ID
      if (!reminderId?.trim()) {
        throw new Error('Reminder ID is required for update');
      }

      // Validate reminder data
      this.validateReminderData(reminderData, 'update');

      // Sanitize data
      const sanitizedData: UpdateReminderRequest = {};
      
      if (reminderData.title !== undefined) {
        sanitizedData.title = reminderData.title.trim();
      }
      if (reminderData.text !== undefined) {
        sanitizedData.text = reminderData.text?.trim() || '';
      }
      if (reminderData.notes !== undefined) {
        sanitizedData.notes = reminderData.notes?.trim() || '';
      }
      if (reminderData.date !== undefined) {
        sanitizedData.date = reminderData.date;
      }
      if (reminderData.type !== undefined) {
        sanitizedData.type = reminderData.type;
      }
      if (reminderData.priority !== undefined) {
        sanitizedData.priority = reminderData.priority;
      }
      if (reminderData.isCompleted !== undefined) {
        sanitizedData.isCompleted = reminderData.isCompleted;
      }
      if (reminderData.isRecurring !== undefined) {
        sanitizedData.isRecurring = reminderData.isRecurring;
      }
      if (reminderData.recurringPattern !== undefined) {
        sanitizedData.recurringPattern = reminderData.recurringPattern;
      }
      if (reminderData.reminderTime !== undefined) {
        sanitizedData.reminderTime = reminderData.reminderTime;
      }
      if (reminderData.status !== undefined) {
        sanitizedData.status = reminderData.status;
      }
      if (reminderData.associations !== undefined) {
        sanitizedData.associations = reminderData.associations?.map(assoc => ({
          ...assoc,
          notes: assoc.notes?.trim() || '',
          metadata: assoc.metadata || {}
        })) || [];
      }
      if (reminderData.metadata !== undefined) {
        sanitizedData.metadata = reminderData.metadata;
      }

      const updatedReminder = await handleApiCall<Reminder>(async () => {
        const response = await api.patch({
          url: getApiUrl(endpoint.reminders.updateReminder(reminderId)),
          body: sanitizedData,
          auth: true,
        });
        return response;
      });

      showToast("Reminder updated successfully", "success");
      return updatedReminder;
    } catch (error) {
      this.handleError(error, `update reminder ${reminderId}`);
    }
  }

  // Update reminder status/priority via dedicated endpoint
  async updateReminderStatus(
    reminderId: string,
    statusData: { remStat?: Reminder['status']; remPriorStat?: 'urgent' | 'high' | 'medium' | 'low' }
  ): Promise<Reminder> {
    try {
      // Validate reminder ID
      if (!reminderId?.trim()) {
        throw new Error('Reminder ID is required for status update');
      }

      const updatedReminder = await handleApiCall<Reminder>(async () => {
        const response = await api.patch({
          url: getApiUrl(endpoint.reminders.updateReminderStatus(reminderId)),
          body: statusData,
          auth: true,
        });
        return response;
      });

      // Don't show toast here - let the component handle success notifications
      // showToast("Reminder status updated successfully", "success");
      return updatedReminder;
    } catch (error) {
      this.handleError(error, `update reminder status ${reminderId}`);
    }
  }

  // Delete reminder
  async deleteReminder(reminderId: string): Promise<void> {
    try {
      // Validate reminder ID
      if (!reminderId?.trim()) {
        throw new Error('Reminder ID is required for deletion');
      }

      await handleApiCall(async () => {
        const response = await api.delete({
          url: getApiUrl(endpoint.reminders.deleteReminder(reminderId)),
          auth: true,
        });
        return response;
      });

      showToast("Reminder deleted successfully", "success");
    } catch (error) {
      this.handleError(error, `delete reminder ${reminderId}`);
    }
  }

  // Bulk operations
  async bulkUpdateReminders(reminderIds: string[], updateData: UpdateReminderRequest): Promise<void> {
    try {
      if (!reminderIds.length) {
        throw new Error('No reminder IDs provided for bulk update');
      }

      this.validateReminderData(updateData, 'update');

      // Process in batches to avoid overwhelming the server
      const batchSize = 10;
      const batches = [];
      
      for (let i = 0; i < reminderIds.length; i += batchSize) {
        batches.push(reminderIds.slice(i, i + batchSize));
      }

      for (const batch of batches) {
        await Promise.all(
          batch.map(id => this.updateReminder(id, updateData))
        );
      }

      showToast(`${reminderIds.length} reminders updated successfully`, "success");
    } catch (error) {
      this.handleError(error, 'bulk update reminders');
    }
  }

  async bulkDeleteReminders(reminderIds: string[]): Promise<void> {
    try {
      if (!reminderIds.length) {
        throw new Error('No reminder IDs provided for bulk delete');
      }

      // Process in batches
      const batchSize = 10;
      const batches = [];
      
      for (let i = 0; i < reminderIds.length; i += batchSize) {
        batches.push(reminderIds.slice(i, i + batchSize));
      }

      for (const batch of batches) {
        await Promise.all(
          batch.map(id => this.deleteReminder(id))
        );
      }

      showToast(`${reminderIds.length} reminders deleted successfully`, "success");
    } catch (error) {
      this.handleError(error, 'bulk delete reminders');
    }
  }

  // Mark reminder as completed
  async markReminderCompleted(reminderId: string): Promise<Reminder> {
    try {
      return await this.updateReminder(reminderId, {
        isCompleted: true,
        status: 'completed',
      });
    } catch (error) {
      this.handleError(error, `mark reminder ${reminderId} as completed`);
    }
  }

  // Mark reminder as pending
  async markReminderPending(reminderId: string): Promise<Reminder> {
    try {
      return await this.updateReminder(reminderId, {
        isCompleted: false,
        status: 'pending',
      });
    } catch (error) {
      this.handleError(error, `mark reminder ${reminderId} as pending`);
    }
  }

  // Get reminders statistics
  async getRemindersStats(): Promise<{
    total: number;
    pending: number;
    completed: number;
    overdue: number;
    today: number;
    thisWeek: number;
    thisMonth: number;
  }> {
    try {
      // Try to get stats from dedicated endpoint first
      try {
        return await handleApiCall<{
          total: number;
          pending: number;
          completed: number;
          overdue: number;
          today: number;
          thisWeek: number;
          thisMonth: number;
        }>(async () => {
          const response = await api.get<{
            statusCode: number;
            message: string;
            data: {
              total: number;
              pending: number;
              completed: number;
              overdue: number;
              today: number;
              thisWeek: number;
              thisMonth: number;
            };
          }>({
            url: getApiUrl(`${endpoint.reminders.getReminders}/stats`),
            auth: true,
          });
          return response;
        });
      } catch (statsError) {

      }

      // Fallback: calculate stats from reminders list
      const response = await this.getReminders({ limit: 1000 });
      const reminders = response.data.items;

      // Helper function to parse YYYY-MM-DD as local date
      const parseLocalDate = (dateStr: string): Date => {
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
          const [year, month, day] = dateStr.split('-').map(Number);
          return new Date(year, month - 1, day);
        }
        return new Date(dateStr);
      };

      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay());
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

      return {
        total: reminders.length,
        pending: reminders.filter(r => r.status === 'pending').length,
        completed: reminders.filter(r => r.status === 'completed').length,
        // Trust backend status only - no client-side overdue detection
        overdue: reminders.filter(r => r.status === 'overdue').length,
        today: reminders.filter(r => {
          const reminderDate = parseLocalDate(r.date);
          return reminderDate >= today && reminderDate < new Date(today.getTime() + 24 * 60 * 60 * 1000);
        }).length,
        thisWeek: reminders.filter(r => parseLocalDate(r.date) >= startOfWeek).length,
        thisMonth: reminders.filter(r => parseLocalDate(r.date) >= startOfMonth).length,
      };
    } catch (error) {
      this.handleError(error, 'fetch reminders statistics', { toast: false });
    }
  }

  // Helper methods
  private computeReminderStatus(reminder: Reminder): Reminder['status'] {
    // Preserve explicit status from backend (cancelled, completed, overdue)
    if (reminder.status === 'cancelled') {
      return 'cancelled';
    }

    if (reminder.isCompleted || reminder.status === 'completed') {
      return 'completed';
    }

    if (reminder.status === 'overdue') {
      return 'overdue';
    }

    if (this.isReminderOverdue(reminder)) {
      return 'overdue';
    }

    return 'pending';
  }

  private isReminderOverdue(reminder: Reminder): boolean {
    // Don't mark cancelled or completed reminders as overdue
    if (reminder.status === 'cancelled' || reminder.status === 'completed' || reminder.isCompleted) {
      return false;
    }

    const now = new Date();
    // Parse YYYY-MM-DD as local date to avoid timezone issues
    let reminderDate: Date;
    if (/^\d{4}-\d{2}-\d{2}$/.test(reminder.date)) {
      const [year, month, day] = reminder.date.split('-').map(Number);
      reminderDate = new Date(year, month - 1, day);
    } else {
      reminderDate = new Date(reminder.date);
    }
    return reminderDate < now;
  }
}

// Export singleton instance
export const remindersService = new EnhancedRemindersService();
export default remindersService;